import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next';
import { XMLParser } from 'fast-xml-parser';

import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { DecisionsPageFieldsFragment, PageInformationFieldsFragment } from 'api/graphql-types';
import { getServerPageGetDecisions } from 'api/graphql-helper';

import { ROUTES } from 'constants/routes';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IXmlData, mapXmlToAccordionList } from 'utils/xml-helper';
import { getXMLDecision, getXMLSidebarData, IXMLSidebarData } from 'utils/aws-s3-helper';
import { getFormattedDate } from 'utils/date';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { IBasePageProps } from 'types/page';
import { AccordionList, IAccordionListProps } from 'modules/accordion-list';
import { Head } from 'modules/head/head';
import { BaseModule } from 'modules/base-module/base-module';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import Layout from 'modules/layout/layout';
import IntroHeader from 'modules/intro-header/intro-header';

export interface IDecisionsPageProps extends IBasePageProps {
	slug: string;
	xml?: IXmlData;
	pageInformation?: PageInformationFieldsFragment;
	decisionsPage?: DecisionsPageFieldsFragment;
	sidebar?: IXMLSidebarData;
	id?: string;
}

const DecisionsPage = ({
	slug,
	pageInformation,
	decisionsPage,
	xml,
	sidebar,
	id
}: IDecisionsPageProps): React.JSX.Element => {
	const { modules, sidebarCurrentYearTitle, sidebarArchiveTitle, archiveLink } = { ...decisionsPage };
	const { locale } = useRouter();
	const seoModule = decisionsPage?.seo;
	const slugDate = slug && getFormattedDate(new Date(slug), 'dd MMMM yyyy', locale);
	const [accordionData, setAccordionData] = useState<IAccordionListProps | undefined>(undefined);

	useEffect(() => {
		if (xml && xml.Besluitenlijst) {
			setAccordionData(mapXmlToAccordionList(xml.Besluitenlijst));
		}
	}, [xml]);

	return (
		<Layout
			contentId={id}
			typeName={decisionsPage?.__typename}
			navbar={pageInformation?.navbar}
			footer={pageInformation?.footer}
		>
			<Head seoModule={seoModule} seoParent={pageInformation?.seo} />
			<BaseModule
				defaultModuleOptions={{
					backgroundColor: decisionsPage?.header?.backgroundColor || 'white',
					padding: 'none'
				}}
			>
				<IntroHeader
					module={decisionsPage?.header}
					fullPath={slugDate ? `${ROUTES.DECISIONS.slug}/${slugDate}` : ROUTES.DECISIONS.slug}
				/>
			</BaseModule>

			{accordionData && (
				<BaseModule
					defaultModuleOptions={{
						backgroundColor: decisionsPage?.header?.backgroundColor === 'white' ? 'grey' : 'white',
						padding: 'medium'
					}}
				>
					<AccordionList
						{...accordionData}
						link={archiveLink}
						archiveTitle={sidebarArchiveTitle}
						currentYearTitle={sidebarCurrentYearTitle}
						sidebar={sidebar}
						slug={slug}
					/>
				</BaseModule>
			)}

			<ModuleBuilder modules={modules} fullPath={ROUTES.DECISIONS.fullPath} />
		</Layout>
	);
};

export const getServerSideProps: GetServerSideProps<IDecisionsPageProps> = async (ctx) => {
	try {
		const { locale, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug as string;
		const xml = await getXMLDecision(slug);

		if (publicationState === 'PREVIEW') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetDecisions(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const { pageInformation, decisionsPage } = result.props.data;

		const parser = new XMLParser();
		const parsedXml = xml ? parser.parse(xml) : '';

		return {
			props: {
				key: decisionsPage?.data?.id,
				slug,
				pageInformation: pageInformation?.data?.attributes,
				decisionsPage: decisionsPage?.data?.attributes,
				xml: parsedXml,
				sidebar: await getXMLSidebarData(),
				id: decisionsPage?.data?.id,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState
					}
				}
			}
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in agenda page', error);

		return {
			notFound: true
		};
	}
};

export default DecisionsPage;
